import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Plus, Building, Trash2, Star, Check, X, AlertCircle, Loader2, Calendar, DollarSign, Clock, Banknote, Shield, RefreshCw, Link2, Edit2, FileText } from 'lucide-react';
import { paymentsAPI, invoicesAPI, recurringAPI, agreementsAPI } from '../services/api';
import { useAuthStore } from '../context/authStore';
import { useFeatureFlag } from '../context/featureFlagStore';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';
import { formatMoney } from '../utils/format';
import { usePaymentGateway, isHostedGateway, HostedAddMethod } from '../components/payments/CardForm';

/*
 * Adding a card or bank account, for whichever gateway this portal uses.
 * The portal's own forms (Portal*) post the details to the server; on the
 * hosted gateway the details are collected by its secure form in the
 * browser and only the confirmed payment-method id is posted.
 */
function hostedSaver(methodType, companyId) {
  return {
    begin: () => paymentsAPI.beginSavedMethod(methodType, { companyId }),
    save: (data) => (methodType === 'ach' ? paymentsAPI.addBank(data, { companyId }) : paymentsAPI.addCard(data, { companyId })),
  };
}

function AddCardModal(props) {
  const gw = usePaymentGateway();
  if (!isHostedGateway(gw.gateway)) return <PortalAddCardModal {...props} />;
  return <HostedAddMethod methodType="card" publishableKey={gw.publishableKey} stripeAccount={gw.stripeAccount} {...hostedSaver('card', props.companyId)} onSuccess={() => props.onSuccess()} onCancel={props.onClose} />;
}

function AddBankModal(props) {
  const gw = usePaymentGateway();
  if (!isHostedGateway(gw.gateway)) return <PortalAddBankModal {...props} />;
  return <HostedAddMethod methodType="ach" publishableKey={gw.publishableKey} stripeAccount={gw.stripeAccount} {...hostedSaver('ach', props.companyId)} onSuccess={() => props.onSuccess()} onCancel={props.onClose} />;
}

function InlineAddCardForm(props) {
  const gw = usePaymentGateway();
  if (!isHostedGateway(gw.gateway)) return <PortalInlineAddCardForm {...props} />;
  return <HostedAddMethod inline methodType="card" publishableKey={gw.publishableKey} stripeAccount={gw.stripeAccount} {...hostedSaver('card', props.companyId)} onSuccess={() => props.onSuccess()} onCancel={props.onCancel} />;
}

function InlineAddBankForm(props) {
  const gw = usePaymentGateway();
  if (!isHostedGateway(gw.gateway)) return <PortalInlineAddBankForm {...props} />;
  return <HostedAddMethod inline methodType="ach" publishableKey={gw.publishableKey} stripeAccount={gw.stripeAccount} {...hostedSaver('ach', props.companyId)} onSuccess={() => props.onSuccess()} onCancel={props.onCancel} />;
}

// Card brand icons/colors
const cardBrands = {
  visa: { color: 'bg-blue-600', label: 'Visa' },
  mastercard: { color: 'bg-red-500', label: 'Mastercard' },
  amex: { color: 'bg-blue-400', label: 'Amex' },
  discover: { color: 'bg-orange-500', label: 'Discover' },
  unknown: { color: 'bg-neutral-500', label: 'Card' },
};

// Format card number with spaces
function formatCardNumber(value) {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  return parts.length ? parts.join(' ') : value;
}

// Format currency
function formatCurrency(amount) {
  return formatMoney(amount);
}

// Format date
function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function PaymentsPage() {
  const { can, user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);

  // Multi-company support
  const { selectedCompanyId } = useCompanyStore();

  // Feature flags for payment types
  const achEnabled = useFeatureFlag('feature.ach_payments');
  const cardEnabled = useFeatureFlag('feature.one_time_payments');
  const autopayEnabled = useFeatureFlag('feature.autopay');
  const recurringEnabled = useFeatureFlag('feature.recurring_payments');
  const [showPayNow, setShowPayNow] = useState(false);
  const [showAutopayConfig, setShowAutopayConfig] = useState(false);
  const [preSelectedInvoiceId, setPreSelectedInvoiceId] = useState(null);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);

  // Fetch payment methods
  const { data: methodsData, isLoading: methodsLoading } = useQuery({
    queryKey: ['paymentMethods', selectedCompanyId],
    queryFn: () => paymentsAPI.getMethods({ companyId: selectedCompanyId }),
  });

  // Fetch payment history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['paymentHistory', selectedCompanyId],
    queryFn: () => paymentsAPI.getHistory({ limit: 10, companyId: selectedCompanyId }),
  });

  // Fetch autopay settings
  const { data: autopayData } = useQuery({
    queryKey: ['autopay', selectedCompanyId],
    queryFn: () => paymentsAPI.getAutopay({ companyId: selectedCompanyId }),
  });

  // Fetch open invoices for Pay Now modal
  const { data: openInvoicesData } = useQuery({
    queryKey: ['openInvoices', selectedCompanyId],
    queryFn: () => invoicesAPI.getOpen({ companyId: selectedCompanyId }),
  });

  // Fetch recurring payments
  const { data: recurringData, isLoading: recurringLoading } = useQuery({
    queryKey: ['recurringPayments', selectedCompanyId],
    queryFn: () => recurringAPI.list({ companyId: selectedCompanyId }),
  });

  // Fetch agreements for linking
  const { data: agreementsData } = useQuery({
    queryKey: ['agreements', selectedCompanyId],
    queryFn: () => agreementsAPI.list({ companyId: selectedCompanyId }),
  });

  // Fetch payment settings (allowed methods, surcharge configuration)
  const { data: settingsData } = useQuery({
    queryKey: ['paymentSettings'],
    queryFn: () => paymentsAPI.getSettings(),
  });

  const paymentMethods = methodsData?.data?.paymentMethods || [];
  const paymentHistory = historyData?.data?.payments || [];
  const autopay = autopayData?.data?.autopay;
  const openInvoices = openInvoicesData?.data?.invoices || [];
  const recurringPayments = recurringData?.data?.recurringPayments || [];
  const agreements = agreementsData?.data?.agreements || [];
  const paymentSettings = settingsData?.data?.settings || {
    allowCreditCard: true,
    allowACH: true,
    creditCardSurchargeEnabled: true,
    creditCardSurchargeType: 'percentage',
    creditCardSurchargeValue: 4
  };

  // Auto-open Pay Now modal if invoice param is present
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    if (invoiceId && openInvoices.length > 0) {
      // Check if the invoice exists in open invoices
      const invoiceExists = openInvoices.some(inv => String(inv.id) === invoiceId);
      if (invoiceExists) {
        setPreSelectedInvoiceId(parseInt(invoiceId));
        setShowPayNow(true);
        // Clear the URL param
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, openInvoices, setSearchParams]);

  // Delete payment method mutation
  const deleteMethodMutation = useMutation({
    mutationFn: (id) => paymentsAPI.deleteMethod(id, { companyId: selectedCompanyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', selectedCompanyId] });
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id) => paymentsAPI.setDefaultMethod(id, { companyId: selectedCompanyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', selectedCompanyId] });
    },
  });

  // Cancel recurring payment mutation
  const cancelRecurringMutation = useMutation({
    mutationFn: (id) => recurringAPI.cancel(id, { companyId: selectedCompanyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringPayments', selectedCompanyId] });
    },
  });

  // Sync recurring payment status mutation
  const syncRecurringMutation = useMutation({
    mutationFn: (id) => recurringAPI.syncStatus(id, { companyId: selectedCompanyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringPayments', selectedCompanyId] });
    },
  });

  // The customer's role (Company Admin and Billing by default): `payments:
  // manage` adds and removes saved methods and sets up AutoPay, `create` pays.
  // The server enforces both; this decides what is offered.
  // Payments are per company, so the role held at the selected company (or
  // the primary one) decides.
  const paymentsCompanyId = selectedCompanyId || user?.cwCompanyId;
  const canManagePayments = can('payments', 'manage', paymentsCompanyId);
  const canAddMethod = canManagePayments && (achEnabled || cardEnabled);
  // Pay Now offers "add a card" inside the dialog, so a person who may add one
  // can open it with nothing saved yet.
  const canPay = can('payments', 'create', paymentsCompanyId) && (paymentMethods.length > 0 || canAddMethod);

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Payments</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Manage payment methods, view history, and configure AutoPay</p>
        </div>
        <CompanyFilter />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Methods */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold">Payment Methods</h2>
            {canManagePayments && (achEnabled || cardEnabled) && (
              <div className="flex gap-2">
                {achEnabled && (
                  <button
                    className="btn-sm btn-outline"
                    onClick={() => setShowAddBank(true)}
                  >
                    <Banknote className="w-4 h-4" /> Bank
                  </button>
                )}
                {cardEnabled && (
                  <button
                    className="btn-sm btn-primary"
                    onClick={() => setShowAddCard(true)}
                  >
                    <Plus className="w-4 h-4" /> Card
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="card-body">
            {methodsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                <CreditCard className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p>No payment methods on file</p>
                {!canManagePayments && (
                  <p className="text-sm mt-2">Payment methods are added by someone in your company with billing access.</p>
                )}
                {canAddMethod && (
                  <button
                    className="btn-primary mt-4"
                    onClick={() => cardEnabled ? setShowAddCard(true) : setShowAddBank(true)}
                  >
                    Add Payment Method
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <PaymentMethodCard
                    key={method.id}
                    method={method}
                    canManagePayments={canManagePayments}
                    onDelete={() => deleteMethodMutation.mutate(method.id)}
                    onSetDefault={() => setDefaultMutation.mutate(method.id)}
                    isDeleting={deleteMethodMutation.isPending}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* AutoPay */}
        {autopayEnabled && (
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="font-semibold">AutoPay Settings</h2>
              {canManagePayments && autopay && (
                <button
                  className="btn-sm btn-outline"
                  onClick={() => setShowAutopayConfig(true)}
                >
                  Configure
                </button>
              )}
            </div>
            <div className="card-body">
              {autopay?.is_enabled ? (
                <AutopayStatus autopay={autopay} paymentMethods={paymentMethods} companyId={selectedCompanyId} />
              ) : (
                <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900 dark:text-white">AutoPay is disabled</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-400">Enable to automatically pay invoices</p>
                  </div>
                  {canManagePayments && paymentMethods.length > 0 && (
                    <button
                      className="btn-primary"
                      onClick={() => setShowAutopayConfig(true)}
                    >
                      Enable
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Pay Section */}
      {openInvoices.length > 0 && (
        <div className="card mt-6">
          <div className="card-header flex items-center justify-between">
            <div>
              <h2 className="font-semibold dark:text-white">Pay Now</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-400">
                {openInvoices.length} open invoice{openInvoices.length !== 1 ? 's' : ''} -
                Total due: {formatCurrency(openInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0))}
              </p>
              {!canPay && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                  No payment method on file. Someone in your company with billing access can add one.
                </p>
              )}
            </div>
            <button
              className="btn-primary"
              onClick={() => setShowPayNow(true)}
              disabled={!canPay}
              title={canPay ? undefined : 'No payment method on file'}
            >
              <DollarSign className="w-4 h-4" /> Pay Invoices
            </button>
          </div>
        </div>
      )}

      {/* Recurring Payments (ARB Subscriptions) */}
      {recurringEnabled && (
        <div className="card mt-6">
          <div className="card-header flex items-center justify-between">
            <div>
              <h2 className="font-semibold dark:text-white">Recurring Payments</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-400">Automated billing subscriptions</p>
            </div>
            {canManagePayments && paymentMethods.length > 0 && (
              <button
                className="btn-sm btn-primary"
                onClick={() => {
                  setEditingRecurring(null);
                  setShowRecurringModal(true);
                }}
              >
                <Plus className="w-4 h-4" /> Add Recurring
              </button>
            )}
          </div>
          <div className="card-body">
            {recurringLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            ) : recurringPayments.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                <RefreshCw className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p>No recurring payments configured</p>
                {canManagePayments && paymentMethods.length > 0 && (
                  <button
                    className="btn-primary mt-4"
                    onClick={() => {
                      setEditingRecurring(null);
                      setShowRecurringModal(true);
                    }}
                  >
                    Set Up Recurring Payment
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {recurringPayments.map((recurring) => (
                  <RecurringPaymentCard
                    key={recurring.id}
                    recurring={recurring}
                    canManagePayments={canManagePayments}
                    paymentMethods={paymentMethods}
                    onEdit={() => {
                      setEditingRecurring(recurring);
                      setShowRecurringModal(true);
                    }}
                    onCancel={() => {
                      if (confirm('Are you sure you want to cancel this recurring payment? This action cannot be undone.')) {
                        cancelRecurringMutation.mutate(recurring.id);
                      }
                    }}
                    onSync={() => syncRecurringMutation.mutate(recurring.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="card mt-6">
        <div className="card-header">
          <h2 className="font-semibold dark:text-white">Payment History</h2>
        </div>
        <div className="card-body p-0">
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
              <Building className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
              <p>No payment history found</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Invoice(s)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id}>
                    <td className="whitespace-nowrap">{formatDate(payment.created_at)}</td>
                    <td className="font-medium">{formatCurrency(payment.amount)}</td>
                    <td>
                      {payment.method_type === 'card' ? (
                        <span className="text-sm">
                          {cardBrands[payment.card_type]?.label || 'Card'} ****{payment.last_four}
                        </span>
                      ) : payment.method_type === 'ach' ? (
                        <span className="text-sm">
                          Bank ****{payment.account_last_four}
                        </span>
                      ) : (
                        <span className="text-sm text-neutral-500 dark:text-neutral-400">-</span>
                      )}
                    </td>
                    <td>
                      <span className="text-sm">
                        {payment.invoice_ids?.join(', ') || '-'}
                      </span>
                    </td>
                    <td>
                      <PaymentStatusBadge status={payment.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddCard && (
        <AddCardModal
          onClose={() => setShowAddCard(false)}
          onSuccess={() => {
            setShowAddCard(false);
            queryClient.invalidateQueries({ queryKey: ['paymentMethods', selectedCompanyId] });
          }}
          companyId={selectedCompanyId}
        />
      )}

      {showAddBank && (
        <AddBankModal
          onClose={() => setShowAddBank(false)}
          onSuccess={() => {
            setShowAddBank(false);
            queryClient.invalidateQueries({ queryKey: ['paymentMethods', selectedCompanyId] });
          }}
          companyId={selectedCompanyId}
        />
      )}

      {showPayNow && (
        <PayNowModal
          onClose={() => {
            setShowPayNow(false);
            setPreSelectedInvoiceId(null);
          }}
          onSuccess={() => {
            setShowPayNow(false);
            setPreSelectedInvoiceId(null);
            queryClient.invalidateQueries({ queryKey: ['paymentHistory', selectedCompanyId] });
            queryClient.invalidateQueries({ queryKey: ['openInvoices', selectedCompanyId] });
          }}
          invoices={openInvoices}
          paymentMethods={paymentMethods}
          preSelectedInvoiceId={preSelectedInvoiceId}
          achEnabled={achEnabled && paymentSettings.allowACH}
          cardEnabled={cardEnabled && paymentSettings.allowCreditCard}
          paymentSettings={paymentSettings}
          companyId={selectedCompanyId}
        />
      )}

      {showAutopayConfig && (
        <AutopayConfigModal
          onClose={() => setShowAutopayConfig(false)}
          onSuccess={() => {
            setShowAutopayConfig(false);
            queryClient.invalidateQueries({ queryKey: ['autopay', selectedCompanyId] });
          }}
          autopay={autopay}
          paymentMethods={paymentMethods}
          companyId={selectedCompanyId}
        />
      )}

      {showRecurringModal && (
        <RecurringPaymentModal
          onClose={() => {
            setShowRecurringModal(false);
            setEditingRecurring(null);
          }}
          onSuccess={() => {
            setShowRecurringModal(false);
            setEditingRecurring(null);
            queryClient.invalidateQueries({ queryKey: ['recurringPayments', selectedCompanyId] });
          }}
          recurring={editingRecurring}
          paymentMethods={paymentMethods}
          agreements={agreements}
          paymentSettings={paymentSettings}
          companyId={selectedCompanyId}
        />
      )}
    </div>
  );
}

// Payment Method Card Component
function PaymentMethodCard({ method, canManagePayments, onDelete, onSetDefault, isDeleting }) {
  const isCard = method.payment_type === 'card';
  const brand = cardBrands[method.card_type] || cardBrands.unknown;

  return (
    <div className={`flex items-center justify-between p-4 rounded-lg border ${
      method.is_default ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' : 'border-neutral-200 dark:border-neutral-700'
    }`}>
      <div className="flex items-center gap-3">
        {isCard ? (
          <div className={`w-10 h-6 rounded ${brand.color} flex items-center justify-center`}>
            <CreditCard className="w-4 h-4 text-white" />
          </div>
        ) : (
          <div className="w-10 h-6 rounded bg-green-600 flex items-center justify-center">
            <Banknote className="w-4 h-4 text-white" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-neutral-900 dark:text-white">
              {method.friendly_name || (isCard
                ? `${brand.label} ending in ${method.last_four}`
                : `Bank ending in ${method.account_last_four}`
              )}
            </p>
            {method.is_default && (
              <span className="badge-primary">
                <Star className="w-3 h-3 mr-1" /> Default
              </span>
            )}
          </div>
          {isCard && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-400">
              Expires {method.exp_month}/{method.exp_year}
            </p>
          )}
          {!isCard && method.bank_name && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-400">{method.bank_name}</p>
          )}
        </div>
      </div>

      {canManagePayments && (
        <div className="flex items-center gap-2">
          {!method.is_default && (
            <button
              className="btn-sm btn-ghost"
              onClick={onSetDefault}
              title="Set as default"
            >
              <Star className="w-4 h-4" />
            </button>
          )}
          <button
            className="btn-sm btn-ghost text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
            onClick={onDelete}
            disabled={isDeleting}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// Payment Status Badge
function PaymentStatusBadge({ status }) {
  const statusStyles = {
    completed: 'badge-success',
    pending: 'badge-warning',
    failed: 'badge-danger',
    refunded: 'badge-neutral',
    voided: 'badge-neutral',
    requires_action: 'badge-warning',
  };
  // A saved-card payment the bank asked the customer to confirm (Phase 7d).
  const label = status === 'requires_action' ? 'Needs your confirmation' : status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span className={statusStyles[status] || 'badge-neutral'}>
      {label}
    </span>
  );
}

/** A YYYY-MM-DD is a calendar date: read it at noon UTC so no timezone moves it a day. */
function formatCalendarDate(value) {
  if (!value) return '';
  return new Date(`${String(value).slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
}

const LAST_RUN_TEXT = {
  succeeded: 'paid',
  failed: 'declined',
  requires_action: 'waiting for you to confirm it with your bank',
  processing: 'being checked',
  skipped: 'not taken',
};

// AutoPay Status Component
function AutopayStatus({ autopay, paymentMethods, companyId }) {
  const linkedMethod = paymentMethods.find(m => m.id === autopay.payment_method_id);
  // When the next charge is, and how the last one went (Phase 7e). Read from
  // the same plan the daily run executes, so the date shown is the date it happens.
  const { data: statusData } = useQuery({
    queryKey: ['autopay-status', companyId],
    queryFn: () => paymentsAPI.getAutopayStatus({ companyId }),
  });
  const status = statusData?.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
        <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
        <div>
          <p className="font-medium text-green-800 dark:text-green-300">AutoPay is enabled</p>
          <p className="text-sm text-green-600 dark:text-green-400">
            {autopay.pay_all_invoices
              ? 'All invoices will be paid automatically'
              : 'Selected agreements will be paid automatically'
            }
          </p>
        </div>
      </div>

      {status && !status.chargingEnabled && (
        <div className="flex gap-2 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-700/50 text-sm text-neutral-600 dark:text-neutral-300">
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Your settings are saved. Automatic payments have not started yet for this portal, so nothing is being charged — please keep paying invoices as usual.</span>
        </div>
      )}
      {status?.chargingEnabled && status.nextCharge && (
        <div className="text-sm text-neutral-700 dark:text-neutral-200">
          <p>
            <span className="font-medium">Next charge:</span> {formatCalendarDate(status.nextCharge.date)} for {formatCurrency(status.nextCharge.amount)}
            {' '}({status.nextCharge.invoiceCount} invoice{status.nextCharge.invoiceCount === 1 ? '' : 's'}
            {status.nextCharge.surcharge > 0 ? `, including a ${formatCurrency(status.nextCharge.surcharge)} card surcharge` : ''})
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Based on the invoices open today. Invoices paid before then are not charged.</p>
        </div>
      )}
      {status?.chargingEnabled && !status.nextCharge && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No charge is scheduled — nothing is due.</p>
      )}
      {status?.attention === 'payment_method' && (
        <div className="flex gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>The payment method chosen for AutoPay is no longer available. Choose another under Configure.</span>
        </div>
      )}
      {status?.attention === 'declined' && (
        <div className="flex gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>AutoPay is paused because the last few payments were declined. Update your payment method under Configure to start it again.</span>
        </div>
      )}
      {status?.lastRun && (
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          <span className="font-medium">Last automatic payment:</span> {formatCalendarDate(status.lastRun.date)}
          {status.lastRun.amount !== null ? `, ${formatCurrency(status.lastRun.amount)}` : ''} — {status.lastRun.reason === 'over_maximum' ? 'not taken: over your maximum' : LAST_RUN_TEXT[status.lastRun.status] || status.lastRun.status}
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-neutral-500 dark:text-neutral-400">Payment Method</p>
          <p className="font-medium dark:text-white">
            {linkedMethod?.friendly_name || 'Not set'}
          </p>
        </div>
        {autopay.max_amount && (
          <div>
            <p className="text-neutral-500">Maximum Amount</p>
            <p className="font-medium">{formatCurrency(autopay.max_amount)}</p>
          </div>
        )}
        {autopay.day_of_month && (
          <div>
            <p className="text-neutral-500">Payment Day</p>
            <p className="font-medium">Day {autopay.day_of_month} of each month{autopay.day_of_month > 28 ? ' (the last day in shorter months)' : ''}</p>
          </div>
        )}
        <div>
          <p className="text-neutral-500">Notifications</p>
          <p className="font-medium">
            {autopay.notify_before_charge ? `${autopay.notification_days_before} days before` : 'Disabled'}
          </p>
        </div>
      </div>
    </div>
  );
}

// Add Card Modal
function PortalAddCardModal({ onClose, onSuccess, companyId }) {
  const [formData, setFormData] = useState({
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
    },
    friendlyName: '',
    isDefault: false,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => paymentsAPI.addCard({
      ...data,
      cardNumber: data.cardNumber.replace(/\s/g, ''),
      expirationMonth: parseInt(data.expirationMonth),
      expirationYear: parseInt(data.expirationYear),
    }, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to add card');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i);

  return (
    <ModalOverlay onClose={onClose}>
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold dark:text-white">Add Credit Card</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Card Number</label>
            <input
              type="text"
              className="input"
              placeholder="1234 5678 9012 3456"
              value={formatCardNumber(formData.cardNumber)}
              onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
              maxLength={19}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Month</label>
              <select
                className="input"
                value={formData.expirationMonth}
                onChange={(e) => setFormData({ ...formData, expirationMonth: e.target.value })}
                required
              >
                <option value="">MM</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                    {String(i + 1).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Year</label>
              <select
                className="input"
                value={formData.expirationYear}
                onChange={(e) => setFormData({ ...formData, expirationYear: e.target.value })}
                required
              >
                <option value="">YYYY</option>
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">CVV</label>
              <input
                type="text"
                className="input"
                placeholder="123"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                maxLength={4}
                required
              />
            </div>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h4 className="font-medium mb-3">Billing Address</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.firstName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                  })}
                  required
                />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.lastName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                  })}
                  required
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="label">Address</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.address}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, address: e.target.value }
                })}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="label">City</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, city: e.target.value }
                  })}
                  required
                />
              </div>
              <div>
                <label className="label">State</label>
                <input
                  type="text"
                  className="input"
                  placeholder="TX"
                  maxLength={2}
                  value={formData.billingAddress.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, state: e.target.value.toUpperCase() }
                  })}
                  required
                />
              </div>
              <div>
                <label className="label">ZIP</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.zip}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, zip: e.target.value }
                  })}
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <label className="label">Nickname (optional)</label>
            <input
              type="text"
              className="input"
              placeholder="e.g., Company Visa"
              value={formData.friendlyName}
              onChange={(e) => setFormData({ ...formData, friendlyName: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Set as default payment method</span>
            </label>
          </div>

          <div className="flex items-center gap-2 p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg text-sm text-neutral-600 dark:text-neutral-400">
            <Shield className="w-4 h-4" />
            Your card information is encrypted and securely stored
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Adding...
                </>
              ) : (
                'Add Card'
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

// Add Bank Modal
function PortalAddBankModal({ onClose, onSuccess, companyId }) {
  const [formData, setFormData] = useState({
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking',
    nameOnAccount: '',
    bankName: '',
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
    },
    friendlyName: '',
    isDefault: false,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => paymentsAPI.addBank(data, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to add bank account');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold dark:text-white">Add Bank Account (ACH)</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Name on Account</label>
            <input
              type="text"
              className="input"
              value={formData.nameOnAccount}
              onChange={(e) => setFormData({ ...formData, nameOnAccount: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Routing Number</label>
              <input
                type="text"
                className="input"
                placeholder="9 digits"
                value={formData.routingNumber}
                onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                maxLength={9}
                required
              />
            </div>
            <div>
              <label className="label">Account Number</label>
              <input
                type="text"
                className="input"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Account Type</label>
              <select
                className="input"
                value={formData.accountType}
                onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                required
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>
            <div>
              <label className="label">Bank Name (optional)</label>
              <input
                type="text"
                className="input"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              />
            </div>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h4 className="font-medium mb-3">Billing Address</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.firstName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                  })}
                  required
                />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.lastName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                  })}
                  required
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="label">Address</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.address}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, address: e.target.value }
                })}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="label">City</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, city: e.target.value }
                  })}
                  required
                />
              </div>
              <div>
                <label className="label">State</label>
                <input
                  type="text"
                  className="input"
                  placeholder="TX"
                  maxLength={2}
                  value={formData.billingAddress.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, state: e.target.value.toUpperCase() }
                  })}
                  required
                />
              </div>
              <div>
                <label className="label">ZIP</label>
                <input
                  type="text"
                  className="input"
                  value={formData.billingAddress.zip}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, zip: e.target.value }
                  })}
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <label className="label">Nickname (optional)</label>
            <input
              type="text"
              className="input"
              placeholder="e.g., Business Checking"
              value={formData.friendlyName}
              onChange={(e) => setFormData({ ...formData, friendlyName: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Set as default payment method</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Adding...
                </>
              ) : (
                'Add Bank Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

// Helper function to calculate credit card surcharge based on settings
function calculateSurcharge(amount, settings) {
  if (!settings?.creditCardSurchargeEnabled) return 0;
  if (settings.creditCardSurchargeType === 'fixed') {
    return settings.creditCardSurchargeValue || 0;
  }
  // percentage
  const rate = (settings.creditCardSurchargeValue || 0) / 100;
  return amount * rate;
}

// Helper function to format surcharge display text
function formatSurchargeText(settings) {
  if (!settings?.creditCardSurchargeEnabled) return null;
  if (settings.creditCardSurchargeType === 'fixed') {
    return `$${settings.creditCardSurchargeValue?.toFixed(2) || '0.00'} surcharge`;
  }
  return `${settings.creditCardSurchargeValue || 0}% surcharge`;
}

// Inline Add Card Form (for use within PayNow modal)
function PortalInlineAddCardForm({ onSuccess, onCancel, companyId }) {
  const [formData, setFormData] = useState({
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
    },
    friendlyName: '',
    isDefault: true,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => paymentsAPI.addCard({
      ...data,
      cardNumber: data.cardNumber.replace(/\s/g, ''),
      expirationMonth: parseInt(data.expirationMonth),
      expirationYear: parseInt(data.expirationYear),
    }, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to add card');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i);

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-700">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary-500" />
          Add Credit Card
        </h4>
      </div>

      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="label text-xs">Card Number</label>
          <input
            type="text"
            className="input"
            placeholder="1234 5678 9012 3456"
            value={formatCardNumber(formData.cardNumber)}
            onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
            maxLength={19}
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label text-xs">Month</label>
            <select
              className="input"
              value={formData.expirationMonth}
              onChange={(e) => setFormData({ ...formData, expirationMonth: e.target.value })}
              required
            >
              <option value="">MM</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                  {String(i + 1).padStart(2, '0')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">Year</label>
            <select
              className="input"
              value={formData.expirationYear}
              onChange={(e) => setFormData({ ...formData, expirationYear: e.target.value })}
              required
            >
              <option value="">YYYY</option>
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">CVV</label>
            <input
              type="text"
              className="input"
              placeholder="123"
              value={formData.cvv}
              onChange={(e) => setFormData({ ...formData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              maxLength={4}
              required
            />
          </div>
        </div>

        <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs font-medium text-neutral-600 mb-2">Billing Address</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">First Name</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.firstName}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                })}
                required
              />
            </div>
            <div>
              <label className="label text-xs">Last Name</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.lastName}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                })}
                required
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="label text-xs">Address</label>
            <input
              type="text"
              className="input"
              value={formData.billingAddress.address}
              onChange={(e) => setFormData({
                ...formData,
                billingAddress: { ...formData.billingAddress, address: e.target.value }
              })}
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div>
              <label className="label text-xs">City</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.city}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, city: e.target.value }
                })}
                required
              />
            </div>
            <div>
              <label className="label text-xs">State</label>
              <input
                type="text"
                className="input"
                placeholder="TX"
                maxLength={2}
                value={formData.billingAddress.state}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, state: e.target.value.toUpperCase() }
                })}
                required
              />
            </div>
            <div>
              <label className="label text-xs">ZIP</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.zip}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, zip: e.target.value }
                })}
                required
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 p-2 bg-white rounded text-xs text-neutral-500">
          <Shield className="w-3 h-3" />
          Your card information is encrypted and securely stored
        </div>

        <div className="flex gap-3 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary flex-1"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            className={`btn-primary ${onCancel ? 'flex-1' : 'w-full'}`}
            disabled={mutation.isPending || !formData.cardNumber || !formData.expirationMonth || !formData.expirationYear || !formData.cvv}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Adding...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" /> Add Card
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Inline Add Bank Form (for use within PayNow modal)
function PortalInlineAddBankForm({ onSuccess, onCancel, companyId }) {
  const [formData, setFormData] = useState({
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking',
    nameOnAccount: '',
    bankName: '',
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
    },
    friendlyName: '',
    isDefault: true,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => paymentsAPI.addBank(data, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to add bank account');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-700">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium flex items-center gap-2">
          <Banknote className="w-4 h-4 text-green-600" />
          Add Bank Account (ACH)
        </h4>
      </div>

      <div className="p-2 mb-4 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
        No card fee on bank account payments
      </div>

      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="label text-xs">Name on Account</label>
          <input
            type="text"
            className="input"
            value={formData.nameOnAccount}
            onChange={(e) => setFormData({ ...formData, nameOnAccount: e.target.value })}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label text-xs">Routing Number</label>
            <input
              type="text"
              className="input"
              placeholder="9 digits"
              value={formData.routingNumber}
              onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
              maxLength={9}
              required
            />
          </div>
          <div>
            <label className="label text-xs">Account Number</label>
            <input
              type="text"
              className="input"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label text-xs">Account Type</label>
            <select
              className="input"
              value={formData.accountType}
              onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
              required
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Bank Name (optional)</label>
            <input
              type="text"
              className="input"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
            />
          </div>
        </div>

        <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs font-medium text-neutral-600 mb-2">Billing Address</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">First Name</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.firstName}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                })}
                required
              />
            </div>
            <div>
              <label className="label text-xs">Last Name</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.lastName}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                })}
                required
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="label text-xs">Address</label>
            <input
              type="text"
              className="input"
              value={formData.billingAddress.address}
              onChange={(e) => setFormData({
                ...formData,
                billingAddress: { ...formData.billingAddress, address: e.target.value }
              })}
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div>
              <label className="label text-xs">City</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.city}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, city: e.target.value }
                })}
                required
              />
            </div>
            <div>
              <label className="label text-xs">State</label>
              <input
                type="text"
                className="input"
                placeholder="TX"
                maxLength={2}
                value={formData.billingAddress.state}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, state: e.target.value.toUpperCase() }
                })}
                required
              />
            </div>
            <div>
              <label className="label text-xs">ZIP</label>
              <input
                type="text"
                className="input"
                value={formData.billingAddress.zip}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, zip: e.target.value }
                })}
                required
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary flex-1"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            className={`btn-primary ${onCancel ? 'flex-1' : 'w-full'}`}
            disabled={mutation.isPending || !formData.accountNumber || !formData.routingNumber || !formData.nameOnAccount}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Adding...
              </>
            ) : (
              <>
                <Banknote className="w-4 h-4" /> Add Bank Account
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Payment Method Chooser (for selecting card vs bank)
function AddPaymentMethodChooser({ onChooseCard, onChooseBank, onCancel, achEnabled = true, cardEnabled = true, paymentSettings }) {
  // If only one option, this component shouldn't be shown, but handle gracefully
  const showBoth = achEnabled && cardEnabled;
  const surchargeText = formatSurchargeText(paymentSettings);

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-700">
      <h4 className="font-medium mb-4">Add Payment Method</h4>

      <div className={`grid ${showBoth ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-4`}>
        {cardEnabled && (
          <button
            type="button"
            onClick={onChooseCard}
            className="flex flex-col items-center gap-2 p-4 border-2 border-neutral-200 rounded-lg hover:border-primary-500 hover:bg-white transition-colors"
          >
            <CreditCard className="w-8 h-8 text-primary-500" />
            <span className="font-medium">Credit Card</span>
            {surchargeText && <span className="text-xs text-neutral-500">{surchargeText}</span>}
          </button>
        )}

        {achEnabled && (
          <button
            type="button"
            onClick={onChooseBank}
            className="flex flex-col items-center gap-2 p-4 border-2 border-neutral-200 rounded-lg hover:border-green-500 hover:bg-white transition-colors"
          >
            <Banknote className="w-8 h-8 text-green-600" />
            <span className="font-medium">Bank Account</span>
            <span className="text-xs text-green-600">No card fee</span>
          </button>
        )}
      </div>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary w-full"
        >
          <X className="w-4 h-4" /> Cancel
        </button>
      )}
    </div>
  );
}

// Pay Now Modal
function PayNowModal({ onClose, onSuccess, invoices, paymentMethods, preSelectedInvoiceId, achEnabled = true, cardEnabled = true, paymentSettings, companyId }) {
  const queryClient = useQueryClient();
  // If a specific invoice is pre-selected, only select that one; otherwise select all
  const [selectedInvoices, setSelectedInvoices] = useState(
    preSelectedInvoiceId ? [preSelectedInvoiceId] : invoices.map(i => i.id)
  );
  const [selectedMethod, setSelectedMethod] = useState(
    paymentMethods.find(m => m.is_default)?.id || paymentMethods[0]?.id || ''
  );
  const [error, setError] = useState('');
  // addingMethod can be: null (not adding), 'chooser' (picking type), 'card', or 'bank'
  // If only one payment type is enabled, skip the chooser and go directly to that form
  const getInitialAddingMethod = () => {
    if (paymentMethods.length > 0) return null;
    if (cardEnabled && achEnabled) return 'chooser';
    if (cardEnabled) return 'card';
    if (achEnabled) return 'bank';
    return null;
  };
  const [addingMethod, setAddingMethod] = useState(getInitialAddingMethod());
  const [localPaymentMethods, setLocalPaymentMethods] = useState(paymentMethods);

  // The first view is decided when the dialog opens. Opened before the saved
  // methods had loaded (straight after signing in), it saw none and showed the
  // add-a-card form; it never looked again. When saved methods arrive and the
  // person has not started adding one themselves, show the dropdown instead.
  const choseToAdd = useRef(false);
  const userAdd = (m) => { choseToAdd.current = true; setAddingMethod(m); };
  useEffect(() => {
    if (!paymentMethods.length || localPaymentMethods.length) return;
    setLocalPaymentMethods(paymentMethods);
    setSelectedMethod((current) => current || paymentMethods.find(m => m.is_default)?.id || paymentMethods[0]?.id || '');
    if (!choseToAdd.current) setAddingMethod(null);
  }, [paymentMethods, localPaymentMethods.length]);

  const subtotal = invoices
    .filter(inv => selectedInvoices.includes(inv.id))
    .reduce((sum, inv) => sum + (inv.balance || 0), 0);

  // Check if selected payment method is a credit card
  const selectedPaymentMethod = localPaymentMethods.find(m => m.id === selectedMethod);
  const isCardPayment = selectedPaymentMethod?.payment_type === 'card';

  // Handle successful payment method addition
  const handlePaymentMethodAdded = async () => {
    // Refresh payment methods
    const response = await paymentsAPI.getMethods({ companyId });
    const newMethods = response.data.paymentMethods || [];
    setLocalPaymentMethods(newMethods);

    // Select the newly added method (it will be the last one or the default)
    const newMethod = newMethods.find(m => m.is_default) || newMethods[newMethods.length - 1];
    if (newMethod) {
      setSelectedMethod(newMethod.id);
    }

    setAddingMethod(null);

    // Also invalidate the query cache so the main page updates
    queryClient.invalidateQueries({ queryKey: ['paymentMethods', companyId] });
  };

  // Calculate surcharge for credit card payments
  const surchargeAmount = isCardPayment ? calculateSurcharge(subtotal, paymentSettings) : 0;
  const totalAmount = subtotal + surchargeAmount;
  const surchargeText = formatSurchargeText(paymentSettings);

  const mutation = useMutation({
    mutationFn: (data) => paymentsAPI.charge(data, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      // Phase 7d: the bank wants the customer to confirm this one in person.
      // Nothing has been charged yet; the confirmation page finishes it.
      const data = err.response?.data;
      if (data?.requiresAction && data.actionId) {
        window.location.assign(`/payments/confirm/${data.actionId}`);
        return;
      }
      setError(data?.error || 'Payment failed');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate({
      paymentMethodId: selectedMethod,
      amount: totalAmount,
      invoiceIds: selectedInvoices,
      surchargeAmount: surchargeAmount,
    });
  };

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold dark:text-white">Pay Invoices</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Select Invoices</label>
            <div className="max-h-48 overflow-y-auto border border-neutral-200 rounded-lg divide-y">
              {invoices.map((inv) => (
                <label
                  key={inv.id}
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(inv.id)}
                      onChange={() => toggleInvoice(inv.id)}
                      className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
                    />
                    <div>
                      <p className="font-medium">Invoice #{inv.invoiceNumber || inv.id}</p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Due: {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <span className="font-medium">{formatCurrency(inv.balance || 0)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Payment Method Selection or Add Payment Method Form */}
          {addingMethod === 'chooser' ? (
            <AddPaymentMethodChooser
              onChooseCard={() => userAdd('card')}
              onChooseBank={() => userAdd('bank')}
              onCancel={localPaymentMethods.length > 0 ? () => setAddingMethod(null) : null}
              achEnabled={achEnabled}
              cardEnabled={cardEnabled}
              paymentSettings={paymentSettings}
            />
          ) : addingMethod === 'card' ? (
            <InlineAddCardForm
              onSuccess={handlePaymentMethodAdded}
              onCancel={() => {
                if (localPaymentMethods.length > 0) {
                  setAddingMethod(null);
                } else if (achEnabled && cardEnabled) {
                  userAdd('chooser');
                }
                // If only card is enabled and no methods, don't allow cancel
              }}
              companyId={companyId}
            />
          ) : addingMethod === 'bank' ? (
            <InlineAddBankForm
              onSuccess={handlePaymentMethodAdded}
              onCancel={() => {
                if (localPaymentMethods.length > 0) {
                  setAddingMethod(null);
                } else if (achEnabled && cardEnabled) {
                  userAdd('chooser');
                }
                // If only ACH is enabled and no methods, don't allow cancel
              }}
              companyId={companyId}
            />
          ) : (
            <div>
              <label className="label">Payment Method</label>
              <select
                className="input"
                value={selectedMethod}
                onChange={(e) => setSelectedMethod(e.target.value)}
                required
              >
                {localPaymentMethods.map((method) => (
                  <option key={method.id} value={method.id}>
                    {method.friendly_name || (method.payment_type === 'card'
                      ? `${cardBrands[method.card_type]?.label || 'Card'} ****${method.last_four}`
                      : `Bank ****${method.account_last_four}`
                    )}
                  </option>
                ))}
              </select>
              {(achEnabled || cardEnabled) && (
                <button
                  type="button"
                  onClick={() => {
                    if (cardEnabled && achEnabled) {
                      userAdd('chooser');
                    } else if (cardEnabled) {
                      userAdd('card');
                    } else if (achEnabled) {
                      userAdd('bank');
                    }
                  }}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add a new payment method
                </button>
              )}
            </div>
          )}

          {/* Only show payment summary when not adding a payment method */}
          {!addingMethod && (
            <>
              <div className="p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600 dark:text-neutral-300">Subtotal</span>
                  <span className="text-neutral-900 dark:text-white">{formatCurrency(subtotal)}</span>
                </div>
                {isCardPayment && surchargeAmount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-neutral-500 dark:text-neutral-400">Card fee ({surchargeText})</span>
                    <span className="text-neutral-600 dark:text-neutral-300">{formatCurrency(surchargeAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-lg font-semibold pt-2 border-t border-neutral-200 dark:border-neutral-700">
                  <span>Total to Pay</span>
                  <span className="text-primary-600">{formatCurrency(totalAmount)}</span>
                </div>
                {isCardPayment && surchargeAmount > 0 && (
                  <p className="text-xs text-neutral-500 mt-1">
                    A card fee of {surchargeText} applies to card payments. Paying by bank account (ACH) avoids it.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={mutation.isPending || selectedInvoices.length === 0}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4" /> Pay {formatCurrency(totalAmount)}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </ModalOverlay>
  );
}

// AutoPay Config Modal
function AutopayConfigModal({ onClose, onSuccess, autopay, paymentMethods, companyId }) {
  const [formData, setFormData] = useState({
    paymentMethodId: autopay?.payment_method_id || paymentMethods.find(m => m.is_default)?.id || '',
    isEnabled: autopay?.is_enabled ?? true,
    payAllInvoices: autopay?.pay_all_invoices ?? true,
    maxAmount: autopay?.max_amount || '',
    dayOfMonth: autopay?.day_of_month || '',
    notifyBeforeCharge: autopay?.notify_before_charge ?? true,
    notifyAfterCharge: autopay?.notify_after_charge ?? true,
    notificationDaysBefore: autopay?.notification_days_before || 3,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => paymentsAPI.setAutopay({
      ...data,
      maxAmount: data.maxAmount ? parseFloat(data.maxAmount) : null,
      dayOfMonth: data.dayOfMonth ? parseInt(data.dayOfMonth) : null,
    }, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to save settings');
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => paymentsAPI.disableAutopay({ companyId }),
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold dark:text-white">Configure AutoPay</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Payment Method</label>
            <select
              className="input"
              value={formData.paymentMethodId}
              onChange={(e) => setFormData({ ...formData, paymentMethodId: e.target.value })}
              required
            >
              <option value="">Select a payment method</option>
              {paymentMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.friendly_name || (method.payment_type === 'card'
                    ? `${cardBrands[method.card_type]?.label || 'Card'} ****${method.last_four}`
                    : `Bank ****${method.account_last_four}`
                  )}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Maximum Amount (optional)</label>
              <input
                type="number"
                className="input"
                placeholder="No limit"
                min="0"
                step="0.01"
                value={formData.maxAmount}
                onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
              />
              <p className="text-xs text-neutral-500 mt-1">Leave empty for no limit</p>
            </div>
            <div>
              <label className="label">Day of Month (optional)</label>
              <select
                className="input"
                value={formData.dayOfMonth}
                onChange={(e) => setFormData({ ...formData, dayOfMonth: e.target.value })}
              >
                <option value="">On due date</option>
                {Array.from({ length: 31 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">In a shorter month, the last day of the month.</p>
            </div>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h4 className="font-medium mb-3">Notifications</h4>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifyBeforeCharge}
                  onChange={(e) => setFormData({ ...formData, notifyBeforeCharge: e.target.checked })}
                  className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm">Notify before charging</span>
              </label>
              {formData.notifyBeforeCharge && (
                <div className="ml-6">
                  <select
                    className="input"
                    value={formData.notificationDaysBefore}
                    onChange={(e) => setFormData({ ...formData, notificationDaysBefore: parseInt(e.target.value) })}
                  >
                    {[1, 2, 3, 5, 7, 14].map((days) => (
                      <option key={days} value={days}>{days} day{days !== 1 ? 's' : ''} before</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifyAfterCharge}
                  onChange={(e) => setFormData({ ...formData, notifyAfterCharge: e.target.checked })}
                  className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm">Notify after payment is processed</span>
              </label>
            </div>
          </div>

          <div className="flex justify-between pt-4">
            {autopay?.is_enabled && (
              <button
                type="button"
                className="btn-danger"
                onClick={() => disableMutation.mutate()}
                disabled={disableMutation.isPending}
              >
                {disableMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Disable AutoPay'
                )}
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

// Modal Overlay Component
function ModalOverlay({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {children}
    </div>
  );
}

// Recurring Payment Card Component
function RecurringPaymentCard({ recurring, canManagePayments, paymentMethods, onEdit, onCancel, onSync }) {
  const linkedMethod = paymentMethods.find(m => m.id === recurring.payment_method_id);

  const statusColors = {
    active: 'badge-success',
    suspended: 'badge-warning',
    cancelled: 'badge-danger',
    expired: 'badge-neutral',
    terminated: 'badge-danger',
  };

  return (
    <div className="p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-neutral-900 dark:text-white">{recurring.name}</h3>
              <span className={`badge ${statusColors[recurring.status] || 'badge-neutral'}`}>
                {recurring.status}
              </span>
            </div>
            <p className="text-lg font-semibold text-neutral-900 dark:text-white mt-1">
              {formatCurrency(recurring.amount)}
              <span className="text-sm font-normal text-neutral-500">
                {' '}/ {recurring.interval_length === 1 ? '' : recurring.interval_length + ' '}
                {recurring.interval_unit === 'months' ? 'month' : 'day'}
                {recurring.interval_length > 1 ? 's' : ''}
              </span>
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              {linkedMethod && (
                <span className="flex items-center gap-1">
                  <CreditCard className="w-3 h-3" />
                  {linkedMethod.payment_type === 'card'
                    ? `${cardBrands[linkedMethod.card_type]?.label || 'Card'} ****${linkedMethod.last_four}`
                    : `Bank ****${linkedMethod.account_last_four}`
                  }
                </span>
              )}
              {recurring.next_payment_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Next: {new Date(recurring.next_payment_date).toLocaleDateString()}
                </span>
              )}
              {recurring.cw_agreement_name && (
                <span className="flex items-center gap-1 text-primary-600">
                  <Link2 className="w-3 h-3" />
                  {recurring.cw_agreement_name}
                </span>
              )}
            </div>
            {recurring.payments_made > 0 && (
              <p className="text-xs text-neutral-400 mt-1">
                {recurring.payments_made} payment{recurring.payments_made !== 1 ? 's' : ''} made
                ({formatCurrency(recurring.total_paid || 0)} total)
              </p>
            )}
          </div>
        </div>

        {canManagePayments && recurring.status === 'active' && (
          <div className="flex items-center gap-1">
            <button
              className="btn-sm btn-ghost"
              onClick={onSync}
              title="Sync status"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              className="btn-sm btn-ghost"
              onClick={onEdit}
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              className="btn-sm btn-ghost text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={onCancel}
              title="Cancel subscription"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Recurring Payment Modal (Create/Edit)
function RecurringPaymentModal({ onClose, onSuccess, recurring, paymentMethods, agreements, paymentSettings, companyId }) {
  const isEditing = !!recurring;

  const [formData, setFormData] = useState({
    paymentMethodId: recurring?.payment_method_id || paymentMethods.find(m => m.is_default)?.id || '',
    name: recurring?.name || '',
    description: recurring?.description || '',
    amount: recurring?.amount || '',
    intervalLength: recurring?.interval_length || 1,
    intervalUnit: recurring?.interval_unit || 'months',
    startDate: recurring?.start_date || new Date().toLocaleDateString('en-CA'),
    totalOccurrences: recurring?.total_occurrences || '',
    cwAgreementId: recurring?.cw_agreement_id || '',
  });
  const [error, setError] = useState('');

  // Get agreement name when selected
  const selectedAgreement = agreements.find(a => a.id === parseInt(formData.cwAgreementId));

  // Check if selected payment method is a credit card
  const selectedPaymentMethod = paymentMethods.find(m => m.id === formData.paymentMethodId);
  const isCardPayment = selectedPaymentMethod?.payment_type === 'card';

  // Calculate surcharge for credit card payments
  const baseAmount = parseFloat(formData.amount) || 0;
  const surchargeAmount = isCardPayment ? calculateSurcharge(baseAmount, paymentSettings) : 0;
  const totalWithSurcharge = baseAmount + surchargeAmount;
  const surchargeText = formatSurchargeText(paymentSettings);

  const createMutation = useMutation({
    mutationFn: (data) => recurringAPI.create(data, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to create recurring payment');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => recurringAPI.update(recurring.id, data, { companyId }),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to update recurring payment');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Use total with surcharge for card payments
    const finalAmount = isCardPayment ? totalWithSurcharge : baseAmount;

    // Build submit data, only including optional fields if they have values
    const submitData = {
      paymentMethodId: formData.paymentMethodId,
      name: formData.name || (selectedAgreement ? `Payment for ${selectedAgreement.name}` : 'Recurring Payment'),
      amount: finalAmount,
      intervalLength: parseInt(formData.intervalLength),
      intervalUnit: formData.intervalUnit,
      startDate: formData.startDate,
    };

    // Only add optional fields if they have values (don't send null)
    if (formData.description) {
      submitData.description = formData.description;
    }
    if (formData.totalOccurrences) {
      submitData.totalOccurrences = parseInt(formData.totalOccurrences);
    }
    if (formData.cwAgreementId) {
      submitData.cwAgreementId = parseInt(formData.cwAgreementId);
      submitData.cwAgreementName = selectedAgreement?.name;
    }

    if (isEditing) {
      // Only send fields that can be updated - recalculate surcharge for edits too
      const editFinalAmount = isCardPayment ? totalWithSurcharge : baseAmount;
      updateMutation.mutate({
        paymentMethodId: submitData.paymentMethodId,
        amount: editFinalAmount,
        name: submitData.name,
      });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <ModalOverlay onClose={onClose}>
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold dark:text-white">
            {isEditing ? 'Edit Recurring Payment' : 'Set Up Recurring Payment'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {!isEditing && (
            <div>
              <label className="label">Link to Agreement (Optional)</label>
              <select
                className="input"
                value={formData.cwAgreementId}
                onChange={(e) => {
                  const agreementId = e.target.value;
                  const agreement = agreements.find(a => a.id === parseInt(agreementId));
                  setFormData({
                    ...formData,
                    cwAgreementId: agreementId,
                    name: agreement ? `Payment for ${agreement.name}` : formData.name,
                    amount: agreement?.billAmount || formData.amount,
                  });
                }}
              >
                <option value="">No agreement (custom recurring)</option>
                {agreements.map((agreement) => (
                  <option key={agreement.id} value={agreement.id}>
                    {agreement.name} - {agreement.type?.name || 'Agreement'}
                    {agreement.billAmount ? ` (${formatCurrency(agreement.billAmount)}/${agreement.billingCycle?.name?.toLowerCase() || 'month'})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                Linking helps track which payments are for which services
              </p>
            </div>
          )}

          {/* Show agreement amount info when an agreement is selected */}
          {selectedAgreement?.billAmount && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">Agreement Billing Amount</p>
                  <p className="text-xs text-blue-600">
                    {selectedAgreement.name} - {selectedAgreement.billingCycle?.name || 'Recurring'}
                  </p>
                </div>
                <p className="text-lg font-semibold text-blue-800">
                  {formatCurrency(selectedAgreement.billAmount)}
                </p>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                You can adjust the amount below if needed
              </p>
            </div>
          )}

          <div>
            <label className="label">Payment Method</label>
            <select
              className="input"
              value={formData.paymentMethodId}
              onChange={(e) => setFormData({ ...formData, paymentMethodId: e.target.value })}
              required
            >
              <option value="">Select a payment method</option>
              {paymentMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.friendly_name || (method.payment_type === 'card'
                    ? `${cardBrands[method.card_type]?.label || 'Card'} ****${method.last_four}`
                    : `Bank ****${method.account_last_four}`
                  )}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Name / Description</label>
            <input
              type="text"
              className="input"
              placeholder="e.g., Monthly IT Support"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              maxLength={50}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <input
                  type="number"
                  className="input pl-7"
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
            </div>
            {!isEditing && (
              <div>
                <label className="label">Start Date</label>
                <input
                  type="date"
                  className="input"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  min={new Date().toLocaleDateString('en-CA')}
                  required
                />
              </div>
            )}
          </div>

          {/* Credit card surcharge info */}
          {isCardPayment && baseAmount > 0 && surchargeAmount > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-amber-700">Base Amount</span>
                  <span className="text-amber-800">{formatCurrency(baseAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">Card fee ({surchargeText})</span>
                  <span className="text-amber-800">{formatCurrency(surchargeAmount)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 border-t border-amber-200">
                  <span className="text-amber-800">Amount to Charge</span>
                  <span className="text-amber-900">{formatCurrency(totalWithSurcharge)}</span>
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                A card fee of {surchargeText} applies to card payments. Paying by bank account (ACH) avoids it.
              </p>
            </div>
          )}

          {!isEditing && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Billing Frequency</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      className="input w-20"
                      min="1"
                      max="365"
                      value={formData.intervalLength}
                      onChange={(e) => setFormData({ ...formData, intervalLength: e.target.value })}
                      required
                    />
                    <select
                      className="input flex-1"
                      value={formData.intervalUnit}
                      onChange={(e) => setFormData({ ...formData, intervalUnit: e.target.value })}
                    >
                      <option value="months">Month(s)</option>
                      <option value="days">Day(s)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="label">Total Payments (Optional)</label>
                  <input
                    type="number"
                    className="input"
                    placeholder="Unlimited"
                    min="1"
                    value={formData.totalOccurrences}
                    onChange={(e) => setFormData({ ...formData, totalOccurrences: e.target.value })}
                  />
                  <p className="text-xs text-neutral-500 mt-1">Leave empty for unlimited</p>
                </div>
              </div>
            </>
          )}

          {isEditing && recurring.cw_agreement_name && (
            <div className="p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Linked Agreement</p>
              <p className="font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary-500" />
                {recurring.cw_agreement_name}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create Recurring Payment'
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}
